import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import auth from '../services/authService';
import { getAssignedPartIds } from '../utils/parts';
import { findPartNode, highlightPartNode, scrollToPartNode } from '../utils/partScroll';

// Asked for in the history entry's state by a link that knows the user has a part in the song: the id
// of the part to land on, or `true` for their first
export const JUMP_TO_MY_PART = 'jumpToMyPart';

/**
 * Lands the song view on one of the user's parts in the hashed song, rather than its top, and rings it.
 *
 * The part asked for, else the first of theirs that is on the page: overview mode leaves some out, and
 * a repeat it has left out falls back to its original. Once only — the request is cleared off the
 * history entry, so a reload, or coming back to it, starts at the top as usual.
 */
const useJumpToMyPart = (songs, ready) => {
  const history = useHistory();
  const requested = history.location.state?.[JUMP_TO_MY_PART];
  const { hash } = history.location;
  const song = songs[hash ? parseInt(hash.substring(1)) - 1 : 0];

  useEffect(() => {
    if (!requested || !ready || !song) return undefined;

    // Once the page has laid out, as continuous view's own jump to a song waits for
    const timer = setTimeout(() => {
      const asked = typeof requested === 'string' ? [requested] : [];

      const node = [...asked, ...getAssignedPartIds(song, auth.getCurrentUser()?._id)]
        .flatMap((partId) => [partId, song.parts.find((part) => part.id === partId)?.duplicateId])
        .filter(Boolean)
        .map((partId) => findPartNode(partId, song._id))
        .find(Boolean);

      if (node) {
        scrollToPartNode(node);
        highlightPartNode(node);
      }

      const { [JUMP_TO_MY_PART]: done, ...state } = history.location.state;

      history.replace({ ...history.location, state });
    }, 100);

    return () => clearTimeout(timer);
  }, [requested, ready, song, history]);
};

export default useJumpToMyPart;
